import { useState, useRef, useEffect } from 'react';
import Modal from '../common/Modal';
import { useTotpSetup, useTotpConfirm } from '../../hooks/useTotp';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function TOTPSetupModal({ isOpen, onClose, onSuccess }: Props) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const setupStartedRef = useRef(false);

  const setupMutation = useTotpSetup();
  const confirmMutation = useTotpConfirm();

  // Handle setup initiation via effect with proper ref tracking
  useEffect(() => {
    if (isOpen && !setupStartedRef.current && !setupMutation.data) {
      setupStartedRef.current = true;
      setupMutation.mutate(undefined);
    }
  }, [isOpen, setupMutation]);

  const handleVerify = () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError('');
    confirmMutation.mutate(code, {
      onSuccess: () => {
        setIsSuccess(true);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Invalid code');
      },
    });
  };

  const handleClose = () => {
    if (!confirmMutation.isPending) {
      // Reset state for next time modal opens
      setupStartedRef.current = false;
      setCode('');
      setError('');
      setIsSuccess(false);
      setupMutation.reset();
      confirmMutation.reset();
      onClose();
    }
  };

  // Determine what to show based on mutation states
  const isLoading = setupMutation.isPending || (!setupMutation.data && !setupMutation.error);
  const hasSetupError = setupMutation.error && !setupMutation.data;
  const showScan = setupMutation.data && !isSuccess;
  const isVerifying = confirmMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Set Up Two-Factor Authentication">
      {isLoading && !hasSetupError && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {hasSetupError && (
        <div className="space-y-4">
          <p className="text-sm text-red-600">
            {setupMutation.error instanceof Error ? setupMutation.error.message : 'Failed to start setup'}
          </p>
          <button
            onClick={handleClose}
            className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
          >
            Close
          </button>
        </div>
      )}

      {showScan && !isVerifying && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Scan this QR code with your authenticator app (like Google Authenticator or Authy).
          </p>

          {/* QR Code */}
          <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
            <img src={setupMutation.data.qr_data_uri} alt="TOTP QR Code" className="w-48 h-48" />
          </div>

          {/* Manual entry */}
          <div className="text-center">
            <p className="text-xs text-gray-500 mb-1">Or enter this code manually:</p>
            <code className="text-sm bg-gray-100 px-3 py-1 rounded font-mono select-all">
              {setupMutation.data.secret}
            </code>
          </div>

          {/* Verification code input */}
          <div>
            <label htmlFor="totp-code" className="block text-sm font-medium text-gray-700 mb-1">
              Enter the 6-digit code from your app
            </label>
            <input
              id="totp-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoComplete="one-time-code"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleVerify}
              disabled={code.length !== 6}
              className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed rounded-md font-medium transition-colors"
            >
              Verify & Enable
            </button>
          </div>
        </div>
      )}

      {isVerifying && (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4" />
          <p className="text-gray-600">Verifying code...</p>
        </div>
      )}

      {isSuccess && (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium">Two-factor authentication enabled!</p>
        </div>
      )}
    </Modal>
  );
}
